/**
 * CapacityBanner — site-wide "at full capacity" announcement bar.
 *
 * Renders a fixed bar above every marketing surface stating that new
 * registrations are closed, with a link to the Solutions microsite for
 * visitors who want a custom agent built instead.
 *
 * LAYOUT CONTRACT (the reason this is more than a <div>):
 * The site has two independent fixed headers pinned at top:0 — the main
 * `Navbar` (Tailwind `fixed w-full top-0`) and `.taaft-minimal-nav` on the
 * TAAFT landing. A bar at top:0 would sit *underneath* both of them. So
 * instead of hardcoding an offset, this component measures its own rendered
 * height and publishes it as the `--capacity-banner-h` CSS variable on
 * <html>, and adds `has-capacity-banner` to <body>. CapacityBanner.css then
 * shifts both headers down and pads the document by that variable.
 *
 * Measuring (rather than assuming 44px) matters because the copy wraps to
 * two lines on narrow viewports and the Arabic translation is a different
 * length — a hardcoded offset would clip the navbar in exactly those cases.
 *
 * Suppressed on the authenticated app surfaces (/platform and friends):
 * the message is for prospective users, and those routes have their own
 * chrome that this bar would fight with.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { buildAgentUrl } from "@/shared/lib/solutionsUrl";
import "./CapacityBanner.css";

/**
 * Route prefixes that never show the banner. These are the signed-in app
 * surfaces — /platform is the SPA the user explicitly excluded, and the
 * rest are the other AuthenticatedLayout routes where a "no new signups"
 * notice is noise for someone who already has an account.
 */
const SUPPRESSED_PREFIXES = [
  "/platform",
  "/dashboard",
  "/onboarding",
  "/billing",
  "/settings",
  "/payment-success",
  "/payment-canceled",
];

/**
 * Session-only dismissal. sessionStorage (not localStorage) is deliberate:
 * the notice reappears on the visitor's next visit, so nobody who returns
 * weeks later — possibly after registration reopens — is permanently
 * stuck with a stale dismissal we can't reach.
 */
const DISMISS_KEY = "midash:capacity_banner_dismissed";

function wasDismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // Private mode / storage disabled — fail toward showing the notice.
    return false;
  }
}

const CapacityBanner: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const barRef = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(wasDismissedThisSession);

  const isRTL = i18n.language === "ar";
  const suppressed = SUPPRESSED_PREFIXES.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`)
  );
  const visible = !dismissed && !suppressed;

  const handleDismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Dismissal still works for this page view even if it can't persist.
    }
    setDismissed(true);
  }, []);

  // Publish the measured height + body class. Runs on every visibility
  // change so hiding the bar fully reverses the offset (otherwise the
  // navbar would stay pushed down on /platform).
  useEffect(() => {
    const root = document.documentElement;
    const clear = () => {
      root.style.setProperty("--capacity-banner-h", "0px");
      document.body.classList.remove("has-capacity-banner");
    };

    if (!visible) {
      clear();
      return clear;
    }

    document.body.classList.add("has-capacity-banner");

    const el = barRef.current;
    if (!el) return clear;

    const sync = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) root.style.setProperty("--capacity-banner-h", `${h}px`);
    };
    sync();

    // The bar's height changes on viewport resize (copy wraps) and on
    // language switch (Arabic is a different length), so observe it
    // rather than measuring once on mount.
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      clear();
    };
  }, [visible, i18n.language, isMobile]);

  if (!visible) return null;

  return (
    <div
      ref={barRef}
      className="capacity-banner"
      role="region"
      aria-label={t("capacityBanner.ariaLabel", "Site capacity notice")}
      dir={isRTL ? "rtl" : "ltr"}
    >
      {/* Phones get a deliberately shorter sentence rather than the desktop
          copy reflowed: the full text wrapped to three ragged lines and broke
          "click here" across two of them. Same meaning, one or two tidy lines,
          and the link stays a single unbroken tap target. */}
      {isMobile ? (
        <p className="capacity-banner__text">
          <span className="capacity-banner__message">
            {t(
              "capacityBanner.messageShort",
              "MiDash is at full capacity — no new sign-ups."
            )}
          </span>{" "}
          <a
            className="capacity-banner__link"
            href={buildAgentUrl("capacity-banner")}
          >
            {t("capacityBanner.linkShort", "Build your own agent")}
          </a>
        </p>
      ) : (
        <p className="capacity-banner__text">
          <span className="capacity-banner__message">
            {t(
              "capacityBanner.message",
              "MiDash is at full capacity — registration of new users is unavailable."
            )}
          </span>{" "}
          <span className="capacity-banner__prompt">
            {t(
              "capacityBanner.prompt",
              "If you want to customize your own agent"
            )}{" "}
            <a
              className="capacity-banner__link"
              href={buildAgentUrl("capacity-banner")}
            >
              {t("capacityBanner.linkLabel", "click here")}
            </a>
            .
          </span>
        </p>
      )}
      <button
        type="button"
        className="capacity-banner__close"
        onClick={handleDismiss}
        aria-label={t("capacityBanner.dismiss", "Dismiss this notice")}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M18 6 6 18" />
          <path d="m6 6 12 12" />
        </svg>
      </button>
    </div>
  );
};

export default CapacityBanner;
